import React from "react";
import { Mail, Users, Eye, Globe, Trophy, Calendar, Star, CheckCircle, Shield, Award } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { Card, CardContent } from "../components/ui/card";

const ACADEMY_LOGO = "https://customer-assets.emergentagent.com/job_4d83666a-aa6c-4973-8e91-b3c87719f098/artifacts/zqkw8qrg_sports-logo-generator-for-a-soccer-team-league-5384a-el1%20%286%29.webp";

const tiers = [
  {
    name: "PLATINUM",
    price: "$30,000",
    available: "1 AVAILABLE",
    position: "FRONT OF JERSEY",
    color: "from-gray-300 via-white to-gray-300",
    borderColor: "border-gray-300",
    textColor: "text-gray-200",
    badgeBg: "bg-gradient-to-r from-gray-300 to-gray-100",
    benefits: [
      "Largest logo on all match jerseys",
      "Website homepage sponsorship",
      "Featured on all major academy social media graphics",
      "4 Dedicated Sponsor Spotlight Campaigns annually",
      "Opportunity to present Academy Player of the Year Awards",
      "Logo on academy marketing & promotional material",
      "Invitation to academy events & presentations",
      "Category exclusivity",
      "Receive a Framed Football X Jersey",
      "A player will be awarded a Full Scholarship"
    ],
    merchandise: ["4 Hoodies", "4 Winter Jackets", "4 Caps", "4 Scarves"]
  },
  {
    name: "GOLD",
    price: "$20,000",
    available: "2 AVAILABLE",
    position: "BACK OF JERSEY",
    color: "from-yellow-500 via-yellow-300 to-yellow-500",
    borderColor: "border-yellow-500",
    textColor: "text-yellow-400",
    badgeBg: "bg-gradient-to-r from-yellow-500 to-yellow-300",
    benefits: [
      "Logo on all match jerseys",
      "Website sponsorship placement",
      "3 Dedicated Sponsor Spotlight Campaigns annually",
      "Recognition at Presentation Day",
      "Logo on academy marketing & promotional material",
      "Invitation to academy events & presentations",
      "Receive a Framed Football X Jersey",
      "A player will be awarded a Full Scholarship"
    ],
    merchandise: ["3 Hoodies", "3 Winter Jackets", "3 Caps", "3 Scarves"]
  },
  {
    name: "SILVER",
    price: "$15,000",
    available: "2 AVAILABLE",
    position: "SLEEVE SPONSOR",
    color: "from-gray-400 via-gray-300 to-gray-400",
    borderColor: "border-gray-400",
    textColor: "text-gray-300",
    badgeBg: "bg-gradient-to-r from-gray-400 to-gray-300",
    benefits: [
      "Logo on all match jerseys",
      "Website sponsorship placement",
      "1 Dedicated Sponsor Spotlight Campaign annually",
      "Recognition at Presentation Day",
      "Logo on academy marketing & promotional material",
      "Invitation to academy events & presentations",
      "Receive a Framed Football X Jersey"
    ],
    merchandise: ["2 Hoodies", "2 Winter Jackets", "2 Caps", "2 Scarves"]
  },
  {
    name: "BRONZE",
    price: "$10,000",
    available: "3 AVAILABLE",
    position: "SHORTS SPONSOR",
    color: "from-orange-700 via-orange-500 to-orange-700",
    borderColor: "border-orange-600",
    textColor: "text-orange-400",
    badgeBg: "bg-gradient-to-r from-orange-700 to-orange-500",
    benefits: [
      "Logo on playing shorts",
      "Website sponsorship placement",
      "Sponsor Recognition Social Media Post annually",
      "Recognition at Presentation Day",
      "Logo on academy marketing & promotional material"
    ],
    merchandise: ["1 Hoodie", "1 Cap", "1 Scarf"]
  }
];

const whyPartner = [
  { icon: <Users className="w-6 h-6" />, text: "Support the development of aspiring footballers" },
  { icon: <Eye className="w-6 h-6" />, text: "Reach hundreds of local football families" },
  { icon: <Globe className="w-6 h-6" />, text: "Gain year-round brand exposure" },
  { icon: <Trophy className="w-6 h-6" />, text: "Align your business with a growing academy" },
  { icon: <Star className="w-6 h-6" />, text: "Showcase your commitment to community sport" },
  { icon: <Shield className="w-6 h-6" />, text: "Be part of the Football X Academy journey" },
];

const Sponsor2027 = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950 via-black to-black"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <img
              src={ACADEMY_LOGO}
              alt="Football X Academy"
              className="h-32 w-auto mx-auto mb-8 drop-shadow-[0_0_25px_rgba(34,211,238,0.3)]"
            />
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              PARTNER WITH <span className="text-cyan-400">FOOTBALL X ACADEMY</span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-bold text-cyan-400 mb-4">IN 2027</h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-6">
              Become a Foundation Sponsor and Help Develop the Next Generation of Footballers.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mt-8">
              {[
                { icon: <Users className="w-5 h-5" />, label: "Reach Over 300+ Football Families" },
                { icon: <Calendar className="w-5 h-5" />, label: "Weekly Match-Day Exposure" },
                { icon: <Globe className="w-5 h-5" />, label: "Social Media Audience" },
                { icon: <Trophy className="w-5 h-5" />, label: "Academy Events & Presentations" },
                { icon: <Eye className="w-5 h-5" />, label: "Academy Website Visitors" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-full px-4 py-2 text-sm">
                  <span className="text-cyan-400">{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            <p className="text-gray-400 text-sm mt-8 max-w-3xl mx-auto italic">
              YOUR PARTNERSHIP PROVIDES YEAR-ROUND EXPOSURE WHILE DIRECTLY SUPPORTING PLAYER DEVELOPMENT, 
              ELITE TRAINING, TOURNAMENTS AND COMMUNITY INITIATIVES.
            </p>
          </div>
        </div>
      </section>

      {/* Sponsorship Tiers */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold">
              SPONSORSHIP <span className="text-cyan-400">PACKAGES</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {tiers.map((tier, index) => (
              <Card
                key={index}
                className={`bg-gray-950 ${tier.borderColor} border-2 hover:scale-[1.02] transition-transform duration-300 overflow-hidden`}
                data-testid={`tier-${tier.name.toLowerCase()}`}
              >
                <CardContent className="p-0">
                  {/* Tier Header */}
                  <div className={`bg-gradient-to-r ${tier.color} p-4 text-center`}>
                    <h3 className="text-2xl font-black text-black">{tier.name}</h3>
                    <p className="text-sm font-bold text-black/70">SPONSOR</p>
                  </div>

                  <div className="p-6">
                    {/* Price */}
                    <div className="text-center mb-4">
                      <p className={`text-3xl font-black ${tier.textColor}`}>{tier.price}</p>
                      <p className="text-xs text-gray-500 uppercase">Per Year</p>
                    </div>

                    {/* Available */}
                    <div className="text-center mb-4">
                      <span className="bg-cyan-500/20 text-cyan-400 text-xs font-bold px-3 py-1 rounded-full">
                        {tier.available}
                      </span>
                    </div>

                    {/* Position */}
                    <div className="bg-gray-900 rounded-lg p-3 text-center mb-6">
                      <p className="text-xs text-gray-500 uppercase">Position</p>
                      <p className="text-sm font-bold text-white">{tier.position}</p>
                    </div>

                    {/* Benefits */}
                    <ul className="space-y-2 mb-6">
                      {tier.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle className={`w-4 h-4 ${tier.textColor} shrink-0 mt-0.5`} />
                          <span className="text-gray-300">{benefit}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Merchandise Package */}
                    <div className="border-t border-gray-800 pt-4">
                      <p className="text-xs text-cyan-400 font-bold uppercase mb-3 tracking-wider">Corporate Merchandise Package</p>
                      <div className="flex flex-wrap gap-2">
                        {tier.merchandise.map((item, i) => (
                          <span key={i} className="bg-gray-900 text-gray-400 text-xs px-2 py-1 rounded">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-20 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold">
              WHY PARTNER WITH <span className="text-cyan-400">FOOTBALL X ACADEMY?</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {whyPartner.map((item, i) => (
              <div key={i} className="text-center p-6 bg-black rounded-xl border border-gray-800 hover:border-cyan-400 transition-colors">
                <div className="text-cyan-400 flex justify-center mb-3">{item.icon}</div>
                <p className="text-sm text-gray-300">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-3xl md:text-4xl font-black mb-2">TOGETHER,</p>
            <p className="text-2xl md:text-3xl font-bold text-cyan-400 mb-2">WE BUILD PLAYERS. WE BUILD FUTURES.</p>
            <p className="text-lg text-gray-400 italic">Developing Players. Building Futures.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-cyan-500">
        <div className="container mx-auto px-4 text-center">
          <img
            src={ACADEMY_LOGO}
            alt="Football X Academy"
            className="h-24 w-auto mx-auto mb-6"
          />
          <h2 className="text-3xl md:text-4xl font-black text-black mb-4">
            BECOME A 2027 FOUNDATION SPONSOR
          </h2>
          <p className="text-lg text-black/80 mb-8 max-w-2xl mx-auto">
            Spaces are limited. Secure your partnership today and make a real impact on the next generation of footballers.
          </p>
          <a
            href="mailto:admin@football-x.com.au?subject=2027 Sponsorship Enquiry - Football X Academy"
            className="inline-flex items-center px-8 py-4 bg-black hover:bg-gray-800 text-white font-bold rounded-lg text-lg transition-colors duration-200"
            data-testid="sponsor-email-cta"
          >
            <Mail className="w-5 h-5 mr-2" />
            admin@football-x.com.au
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Sponsor2027;
